import { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { subDays, subMonths, startOfDay } from 'date-fns';
import { UrlUpdateType } from 'use-query-params';
import clsx from 'clsx';

import { useWindowSize } from 'utils/use-window-size';
import useOutsideClick from 'utils/use-outside-click';

import { Button } from 'components/button';
import { Icon } from 'components/icon/icon';
import { RadioButtonGroup } from 'components/radio-button-group/radio-button-group';
import { Popper } from 'components/core/popper';
import { RadioButtonItem } from 'components/core/radio-button-group';

import SelectIcon from 'public/assets/icons/select.svg';
import CrossIcon from 'public/assets/icons/cross.svg';

import { DATE_FILTER_TYPE, DateFilterRadioButtonItem, DateFilterValue, getDateFilterItems } from './date-filter-data';

import DateFilterPickers from './date-filter-pickers';
import styles from './date-filter-button.module.scss';

type NewValueType<D> = D | ((latestValue: D) => D);

export interface IFilterButtonProps {
	title?: string;
	onChange: (value: DateFilterValue) => void;
	mobileIsOpen?: boolean;
	setMobileIsOpen: (isOpen: boolean) => void;
	value?: DateFilterValue | null;
	dateFilter?: DateFilterValue | null;
	setDateFilter?: (newValue: NewValueType<DateFilterValue | null>, updateType?: UrlUpdateType | undefined) => void;
	useMobilePicker?: boolean;
	initialStartDate?: Date;
	initialEndDate?: Date;
	showDates?: boolean;
}

const MOBILE_SIZE = 768;

const DateFilterButton = (props: IFilterButtonProps): React.JSX.Element => {
	const intl = useIntl();
	const items = getDateFilterItems(intl);

	const [isOpen, setIsOpen] = useState<boolean>(false);
	const [showDates, setShowdates] = useState<boolean>(props.showDates || false);
	const [showDatePickerStart, setShowDatePickerStart] = useState<boolean>(false);
	const [showDatePickerEnd, setShowDatePickerEnd] = useState<boolean>(false);
	const [dateHasError, setDateHasError] = useState<boolean>(false);
	const [startDate, setStartDate] = useState<Date | undefined>(props.initialStartDate);
	const [endDate, setEndDate] = useState<Date | undefined>(props.initialEndDate);

	const [selectedRadioItem, setSelectedRadioItem] = useState<DateFilterRadioButtonItem | undefined>(
		items.find((item) => item.id === props.dateFilter?.type)
	);

	const dropDownRef = useRef<HTMLDivElement>(null);
	const buttonRef = useRef<HTMLSpanElement>(null);
	const mobileMenuRef = useRef<HTMLDivElement>(null);
	const datePickerRef = useRef<any>(null);

	useOutsideClick([dropDownRef, buttonRef, mobileMenuRef, datePickerRef], () => setIsOpen(false), true);
	const windowSize = useWindowSize(100);

	const startDateIsPastEndDate = startDate && endDate && startDate > endDate;

	const handleSubmit = (): void => {
		if (startDateIsPastEndDate || !props.setDateFilter) {
			setDateHasError(true);
			return;
		}

		const dateFilter = {
			startDate,
			endDate,
			type: selectedRadioItem?.id || DATE_FILTER_TYPE.ALL
		};

		setIsOpen(false);
		props.onChange(dateFilter);
	};

	const handleMobileSubmit = (): void => {
		handleSubmit();
		props.setMobileIsOpen(false);
	};

	useEffect(() => {
		if (isOpen && (windowSize.width || 0) < MOBILE_SIZE) {
			document.body.style.overflow = 'hidden';
		} else {
			document.body.style.overflow = 'visible';
		}
	}, [isOpen]);

	const onStartDateClick = (date: Date): void => {
		setStartDate(date);
	};

	const onEndDateClick = (day: Date): void => {
		setEndDate(day);
	};

	const onRadioChange = (selectedItem: RadioButtonItem): void => {
		setSelectedRadioItem(selectedItem as DateFilterRadioButtonItem);
		const newDate = startOfDay(new Date());

		if (selectedItem.id !== DATE_FILTER_TYPE.CUSTOM) {
			setShowDatePickerStart(false);
			setShowDatePickerEnd(false);
			setShowdates(false);
			setDateHasError(false);
		}

		switch (selectedItem.id) {
			case DATE_FILTER_TYPE.ALL:
				setStartDate(undefined);
				setEndDate(undefined);
				break;
			case DATE_FILTER_TYPE.LAST_WEEK:
				setStartDate(subDays(newDate, 7));
				break;
			case DATE_FILTER_TYPE.LAST_MONTH:
				setStartDate(subMonths(newDate, 1));
				break;
			case DATE_FILTER_TYPE.LAST_THREE_MONTHS:
				setStartDate(subMonths(newDate, 3));
				break;
			case DATE_FILTER_TYPE.CUSTOM:
				setStartDate(undefined);
				setEndDate(undefined);
				setShowDatePickerStart(false);
				setShowDatePickerEnd(false);
				setShowdates(true);
				break;
			default:
				break;
		}
	};

	const onEnter = (event: React.KeyboardEvent<HTMLElement>): void => {
		if (event.key === 'Enter') {
			props.setMobileIsOpen(false);
		}
	};

	return (
		<div
			className={styles.filterButton}
			aria-label={`${isOpen ? intl.formatMessage({ id: 'global.open' }) : intl.formatMessage({ id: 'global.closed' })}`}
		>
			<Popper
				id={props.title || ''}
				toggleButton={
					<span className={styles.button} ref={buttonRef}>
						<Button
							variant="tertiary-dark"
							title={props.title}
							onClick={() => {
								setIsOpen(!isOpen);
							}}
							icon={<Icon customIcon={<SelectIcon />} size="16px" />}
							ariaExpanded={isOpen}
						/>
					</span>
				}
				isOpen={isOpen}
				zIndex={10}
			>
				<div className={clsx(styles.collapsibleMenu, styles.datePickerContent)} ref={dropDownRef}>
					<div className={styles.content}>
						<div className={styles.filters}>
							<fieldset>
								<legend className={styles.title}>{props.title}</legend>
								<div>
									<RadioButtonGroup
										id="date-radio"
										items={items}
										onChange={onRadioChange}
										selectedItem={selectedRadioItem}
										singleColumn
									/>
								</div>
							</fieldset>

							<DateFilterPickers
								startDate={startDate}
								endDate={endDate}
								showDatePickerStart={showDatePickerStart}
								showDatePickerEnd={showDatePickerEnd}
								showDates={showDates}
								dateHasError={dateHasError}
								onStartDateClick={onStartDateClick}
								onEndDateClick={onEndDateClick}
								setStartDate={setStartDate}
								setEndDate={setEndDate}
								setShowDatePickerStart={setShowDatePickerStart}
								setShowDatePickerEnd={setShowDatePickerEnd}
							/>

							<div className={styles.bottomBar}>
								<div className={styles.changeButton}>
									<Button
										variant="primary"
										title={intl.formatMessage({ id: 'filterButton.apply' })}
										onClick={handleSubmit}
										fullWidth
									/>
								</div>
							</div>
						</div>
					</div>
				</div>
			</Popper>
			<div
				className={clsx(styles.mobileFilter, styles.datePickerContent, props.mobileIsOpen && styles.mobileFilterOpen)}
			>
				<fieldset>
					<legend className={styles.mobileFilterHeader}>
						<h3 id={intl.formatMessage({ id: 'modules.searchOverview.dateFilter' })}>
							{intl.formatMessage({ id: 'modules.searchOverview.dateFilter' })}
							<span role="button" onClick={() => props.setMobileIsOpen(false)} onKeyUp={onEnter} tabIndex={0}>
								<Icon customIcon={<CrossIcon />} size="14px" />
							</span>
						</h3>
					</legend>

					<div className={clsx(styles.mobileFilterBody, styles.mobileFilterGroup, styles.sortGroupActive)}>
						<div className={styles.mobileRadioGroup}>
							<RadioButtonGroup
								id="date-radio-mobile"
								items={props.useMobilePicker ? items : items.slice(0, -1)}
								onChange={onRadioChange}
								selectedItem={selectedRadioItem}
								singleColumn
							/>

							{props.useMobilePicker && (
								<DateFilterPickers
									startDate={startDate}
									endDate={endDate}
									showDatePickerStart={showDatePickerStart}
									showDatePickerEnd={showDatePickerEnd}
									showDates={showDates}
									dateHasError={dateHasError}
									onStartDateClick={onStartDateClick}
									onEndDateClick={onEndDateClick}
									setStartDate={setStartDate}
									setEndDate={setEndDate}
									setShowDatePickerStart={setShowDatePickerStart}
									setShowDatePickerEnd={setShowDatePickerEnd}
								/>
							)}
						</div>
					</div>
				</fieldset>

				<div className={styles.mobileFilterFooter}>
					<Button title={intl.formatMessage({ id: 'global.showResults' })} onClick={handleMobileSubmit} fullWidth />
				</div>
			</div>
		</div>
	);
};

export { DateFilterButton };
